<script lang="ts" setup>
import { computed, Ref, ref, toRef } from 'vue';
import Chip from 'primevue/chip';
import AddPersonModal from '@/components/AddPersonModal/AddPersonModal.vue';
import { useToast } from 'primevue/usetoast';
import { useParentStudentStore } from '@/store';
import { StudentInterface } from '@/types';
import { EMIT, PERMISSIONS } from '@/enum';
import { can } from '@/composables';

interface StudentPropsInterface {
  tableData?: StudentInterface[];
  parentId?: number;
  loading?: boolean;
}

interface StudentTableInterface {
  id: number;
  display_name: string;
}

const props = defineProps<StudentPropsInterface>();
const emit = defineEmits<{ (e: EMIT.UPDATE): void }>();
const parentStudentStore = useParentStudentStore();
const students: Ref<StudentInterface[] | undefined> = toRef(props, 'tableData');
const studentCount: Ref<number> = computed(() => (students.value ? students.value.length : 0));
const studentModalOpen = ref(false);
const addStudentDataLoading = ref(false);
const isAttach = ref(false);
const toast = useToast();
const studentItems: Ref<{ name: string; value: number }[]> = ref([]);

const doMessage = (name: string) => {
  toast.add({
    severity: 'success',
    summary: isAttach.value ? 'Added' : 'Removed',
    detail: isAttach.value ? 'Student(s) has been added' : `Student ${name} has been removed from Parent`,
    life: 3000,
  });
  emit(EMIT.UPDATE);
};

const createMenuModel = (item: StudentTableInterface) => {
  return [
    {
      label: 'Remove Student',
      command: () => {
        detachStudent([item.id], item.display_name);
      },
      visible: can([PERMISSIONS.PARENT_DETACH_STUDENTS]),
    },
  ].filter((item) => item.visible);
};

const attachStudents: (ids: number[]) => Promise<void> = async (ids) => {
  await parentStudentStore.attachStudents({ students: ids, parentId: props.parentId as number });

  isAttach.value = !isAttach.value;

  doMessage('');
};

const detachStudent: (ids: number[], studentName: string) => Promise<void> = async (ids, studentName) => {
  await parentStudentStore.detachStudent({ students: ids, parentId: props.parentId as number });

  isAttach.value = false;

  doMessage(studentName);
};

const openAddStudents = async () => {
  studentItems.value = [];
  if (can([PERMISSIONS.PARENT_STUDENTS_AVAILABLE_TO_ADD]) && can([PERMISSIONS.PARENT_ATTACH_STUDENTS])) {
    addStudentDataLoading.value = true;
    studentModalOpen.value = true;
    studentItems.value = await parentStudentStore.getAddStudentsModalData(props.parentId as number);
    addStudentDataLoading.value = false;
  } else {
    toast.add({
      severity: 'warning',
      summary: 'Error',
      detail: 'You do not have permission to add students',
      life: 3000,
    });
  }
};

const onCancelAdd = () => (studentModalOpen.value = false);

const onSaveAdd = (students: { name: string; value: number }[]) => {
  let studentIds = students.map((student) => student.value);
  attachStudents(studentIds);
  studentModalOpen.value = false;
};
</script>

<template>
  <Card>
    <template #header>
      <h2>Students</h2>
      <Chip>{{ studentCount }}</Chip>
      <div class="ml-auto">
        <AppButton
          v-if="can([PERMISSIONS.PARENT_ATTACH_STUDENTS])"
          type="text"
          size="small"
          text="Add Students"
          icon-left="add"
          @click="openAddStudents"
        />
      </div>
    </template>
    <template #content>
      <DataTable
        :value="students"
        :global-filter-fields="['name']"
        :scrollable="true"
        loading-icon="logo-spinner"
        :loading="loading"
        data-key="name"
        responsive-layout="scroll"
        scroll-height="440px"
        state-key="session-store-parent-students-table"
        state-storage="session"
        :class="{ 'table-loading': loading }"
      >
        <template v-if="studentCount">
          <Column body-class="mw-20rem" header-class="mw-20rem" field="display_name" header="Student Name" sortable>
            <template #body="{ data }">
              <a class="font-semibold" :href="`/user/${data.id}`" v-text="data.display_name" />
            </template>
          </Column>
          <Column />
          <Column
            body-class="justify-content-center mw-10rem"
            header-class="justify-content-center mw-10rem"
            field="id"
            header="Actions"
          >
            <template #body="{ data }">
              <ActionMenu
                v-if="createMenuModel(data).length > 0"
                :menu-items="createMenuModel(data)"
                icon="more_horiz"
              />
            </template>
          </Column>
        </template>
        <template #empty>Students not assigned</template>
      </DataTable>
      <AddPersonModal
        v-if="studentModalOpen && can([PERMISSIONS.PARENT_STUDENTS_AVAILABLE_TO_ADD])"
        title="Add Students"
        button-text="Add Students"
        placeholder="Search students"
        :loading="addStudentDataLoading"
        :items="studentItems"
        :searchable="true"
        @add-items="onSaveAdd"
        @cancel="onCancelAdd"
      />
    </template>
  </Card>
</template>
